import { connect } from "react-redux";
import ChamberRowClass, { cloneChamberRows } from "../../lib/chamber-row";
import {
    endSystemGeneration,
    startVolumeOptimizer,
} from "../../actions/calculations-actions";
import {
    cleanChamberBed,
    chamberRowsRebuild,
    chamberBedTransformed,
}
    from "../../actions/chamber-bed-actions";
import { getChamberAccessoryMap } from "../../actions/chamber-actions";
import { clearMainManifoldsState, createMainManifold, getManifoldMetadata } from "../../actions/manifold-actions";
import { getUnscaledValue } from "../../actions/calculations-actions";
import BaseComponent from "../../lib/base-component";
import {
    INLET_MANIFOLD_ID,
    OUTLET_MANIFOLD_ID,
    GLOBAL_PROJECT_TYPE_CHAMBER,
} from "../../lib/types";
import CalculationsLib, { getScaledValue } from "../../lib/calculations";
import { calculateManifoldSeparations, applyManifoldSeparations, getManifolds } from "../../actions/manifold-actions";
import { modalAlertActions } from "../../actions/modal-alert.actions";
import { ManifoldUtils } from "../../lib/manifolds/utils";
import { calculatePolygonArea } from "../../lib/chamberBed/utils";

class SystemBuilder extends BaseComponent{
    componentDidUpdate( prevProps ) {
        if (prevProps.generateSystem !== this.props.generateSystem && this.props.generateSystem) {
            this.props.applyManifoldSeparations(this.props.storageVolume);
            this.generateSystem()
                .catch(error => {
                    console.warn("There was an error while generating a new system", error);
                })
                .finally(() => {
                    setTimeout(() => {
                        let {
                            mainManifolds: mainManifoldsMetadata,
                        } = this.props.getManifoldMetadata(this.props.chamberbed.chamberRows);
                        mainManifoldsMetadata.forEach(manifoldMeta => {
                            this.props.createMainManifold(manifoldMeta.letRow, manifoldMeta.letColumn);
                            this.props.endSystemGeneration();
                        });
                    }, 100);
                });
        }
    }

    async generateSystem() {
        if (!( this.props.chamberType && this.props.chamberEndCapType )) return;

        this.props.clearMainManifoldsState();

        const
            chamberHeight = this.getChamberHeight(),
            chamberWidth = this.getChamberWidth(),
            availableWidth = this.getScaledValue(this.props.availableWidth),
            availableLength = this.getScaledValue(this.props.availableLength);

        let
            numberOfRows = Math.ceil(availableWidth / chamberHeight),
            numberOfChambers = Math.ceil(availableLength / chamberWidth),
            storageVolume = this.props.storageVolume;

        if (typeof storageVolume === "string")
            storageVolume = parseFloat(storageVolume);

        let chamberRows = this.buildChamberRows(numberOfRows, numberOfChambers);

        let { inletStructureType, outletStructureType } = this.getStructureTypes(storageVolume);

        // Derived version of manifolds; must start with storage volume
        let { manifoldSeparations } = this.props.calculateManifoldSeparations(storageVolume);

        if (numberOfRows > 0 && numberOfChambers > 0) {
            chamberRows[0].modifyChamber({
                upperCapAccessory: {
                    ...this.props.getChamberAccessoryMap().get(INLET_MANIFOLD_ID),
                    variant: inletStructureType,
                    manifoldId: ManifoldUtils.generateManifoldId(),
                },
            }, 0);
        }

        chamberRows = this.addOutlet(chamberRows, outletStructureType);

        let lessChamberRow = ( chamberRows, numberOfRows ) => {
            return this.addOutlet(chamberRows.slice(0, numberOfRows), outletStructureType);
        };

        let lessChamber = ( chamberRows, numberOfChambers ) => {
            let calculatedBedLength = this.calculatedBedLength(numberOfChambers);

            return this.addOutlet(cloneChamberRows(chamberRows).map(chamberRow => {
                chamberRow.width = calculatedBedLength;
                chamberRow.resizeDimensions();

                chamberRow.modifyChamber({
                    active: true,
                    lowerCapActive: true,
                }, numberOfChambers - 1);

                return chamberRow;
            }), outletStructureType);
        };

        while (
            numberOfRows > 0 &&
            this.getEstimatedChamberBedDimensions(chamberRows, manifoldSeparations).width > availableWidth
            ) {
            chamberRows = lessChamberRow(chamberRows, --numberOfRows);

            if (numberOfRows > 0) {
                manifoldSeparations = this.getManifoldSeparations(chamberRows, manifoldSeparations);
            }
        }

        while (numberOfChambers > 0 &&
            this.getEstimatedChamberBedDimensions(chamberRows, manifoldSeparations).length > availableLength
            ) {
            chamberRows = lessChamber(chamberRows, --numberOfChambers);

            if (numberOfChambers > 0) {
                manifoldSeparations = this.getManifoldSeparations(chamberRows, manifoldSeparations);
            }
        }

        if (numberOfRows === 0 || numberOfChambers === 0) {
            if (numberOfRows === 0 && numberOfChambers === 0) {
                this.props.modalAlertActions({
                    toggle: true,
                    text: this.props.content[this.props.lang].msgRequiresLargerWidthAndLength,
                });
            } else if (numberOfRows === 0) {
                this.props.modalAlertActions({
                    toggle: true,
                    text: this.props.content[this.props.lang].msgRequiresLargerAllowableWidth,
                });
            } else if (numberOfChambers === 0) {
                this.props.modalAlertActions({
                    toggle: true,
                    text: this.props.content[this.props.lang].msgRequiresLargerAllowableLength,
                });
            }

            this.props.chamberBedTransformed({
                scaleX: 1,
                scaleY: 1,
                numOfRows: 0,
                numOfChambers: 0,
            });
            this.props.chamberRowsRebuild([], 0, 0);
            return;
        }

        let
            currentChamberRows = chamberRows.slice(0),
            currentNumberOfRows = numberOfRows;

        currentChamberRows = lessChamberRow(currentChamberRows, --currentNumberOfRows);

        let tempManifoldSeparations = manifoldSeparations;

        while (
            numberOfRows > 0 &&
            numberOfChambers > 0 &&
            storageVolume > 0 &&
            this.getEstimatedTotalVolume(currentChamberRows, tempManifoldSeparations) >= storageVolume
            ) {
            manifoldSeparations = tempManifoldSeparations;
            chamberRows = currentChamberRows;
            numberOfRows = currentNumberOfRows;

            currentChamberRows = lessChamberRow(currentChamberRows, --currentNumberOfRows);
            tempManifoldSeparations = this.getManifoldSeparations(currentChamberRows, tempManifoldSeparations);
        }

        let currentNumberOfChambers = numberOfChambers;

        currentChamberRows = lessChamber(chamberRows, --currentNumberOfChambers);

        tempManifoldSeparations = manifoldSeparations;

        while (
            numberOfRows > 0 &&
            numberOfChambers > 0 &&
            storageVolume > 0 &&
            this.getEstimatedTotalVolume(currentChamberRows, tempManifoldSeparations) >= storageVolume
            ) {
            manifoldSeparations = tempManifoldSeparations;
            chamberRows = currentChamberRows;
            numberOfChambers = currentNumberOfChambers;

            currentChamberRows = lessChamber(currentChamberRows, --currentNumberOfChambers);
            tempManifoldSeparations = this.getManifoldSeparations(currentChamberRows, tempManifoldSeparations);
        }

        this.getManifoldSeparations(chamberRows, manifoldSeparations);

        let newRectangleHeight = this.calculatedBedLength(numberOfChambers);
        let newRectangleWidth = this.calculatedBedWidth(numberOfRows);

        /* Calculate the new scaling */
        let scaleY = 1;
        let scaleX = 1;

        this.props.chamberBedTransformed({
            scaleX: scaleX,
            scaleY: scaleY,
            numOfRows: numberOfRows,
            width: newRectangleHeight,
            height: newRectangleWidth,
            numOfChambers: numberOfChambers,
        });

        this.props.chamberRowsRebuild(chamberRows, numberOfRows, numberOfChambers);
        this.props.cleanChamberBed();
        this.props.startVolumeOptimizer();
    }

    // Get manifold separations of the current "situation"; reapply any values that might be needed to changed
    getManifoldSeparations( chamberRows, manifoldSeparations ) {
        const estimatedTotalVolume = this.getEstimatedTotalVolume(chamberRows, manifoldSeparations);
        const { inletStructureType, outletStructureType } = this.getStructureTypes(estimatedTotalVolume);

        if (chamberRows.length) {
            const inletChamber = chamberRows[0].getFirstActiveChamber();
            const outletChamber = chamberRows[chamberRows.length - 1].getLastActiveChamber();
            if (inletChamber && inletChamber.upperCapAccessory) {
                inletChamber.upperCapAccessory.variant = inletStructureType;  // Guaranteed
            }
            if (outletChamber && outletChamber.lowerCapAccessory) {
                outletChamber.lowerCapAccessory.variant = outletStructureType;
            }
        }

        this.props.applyManifoldSeparations(estimatedTotalVolume);

        return this.props.calculateManifoldSeparations(estimatedTotalVolume).manifoldSeparations;
    }

    getStructureTypes( storageVolume ) {
        let inletStructureType = "round";
        let outletStructureType = "round";

        //let manifoldSizing = getManifoldSizing(storageVolume, this.props.chamberType);
        let manifoldSizing = ManifoldUtils.getManifoldSizingByChamberType(this.props.chamberType.id, storageVolume);

        if (manifoldSizing && manifoldSizing.inletType) {
            inletStructureType = manifoldSizing.inletType;
        }
        if (manifoldSizing && manifoldSizing.outletType) {
            outletStructureType = manifoldSizing.outletType;
        }

        return { inletStructureType, outletStructureType };
    }

    buildChamberRows( numberOfRows, numberOfChambers ) {
        const
            chamberHeight = this.getChamberHeight(),
            chamberWidth = this.getChamberWidth(),
            chamberInstalledOffset = this.getChamberInstalledOffset(),
            endCapHeight = this.getChamberHeight(),
            endCapWidth = this.getEndCapWidth(),
            endCapInstalledOffset = this.getEndCapInstalledOffset(),
            rowSpacing = this.getRowSpacing();

        let calculatedBedLength = this.calculatedBedLength(numberOfChambers);

        let chamberRows = [];

        for (let a = 0; a < numberOfRows; ++a) {
            let chamberRow = new ChamberRowClass();
            chamberRow.row = a;
            chamberRow.x = 0;
            chamberRow.y = a * ( chamberHeight + ( a > 0 && a < numberOfRows ? rowSpacing : 0 ) );
            chamberRow.height = chamberHeight;
            chamberRow.width = calculatedBedLength;
            chamberRow.endCapHeight = endCapHeight;
            chamberRow.endCapWidth = endCapWidth;
            chamberRow.endcapInstalledOffset = endCapInstalledOffset;
            chamberRow.chamberWidth = chamberWidth;
            chamberRow.chamberInstalledOffset = chamberInstalledOffset;
            chamberRow.resizeDimensions();

            for (let b = 0; b < numberOfChambers; ++b) {
                chamberRow.modifyChamber({
                    active: true,
                    upperCapActive: b === 0,
                    lowerCapActive: b === numberOfChambers - 1,
                }, b);
            }
            chamberRows.push(chamberRow);
        }
        return chamberRows;
    }

    calculatedBedLength( numOfChambers ) {
        let chamberWidth = this.getChamberWidth();
        let chamberInstalledOffset = this.getChamberInstalledOffset();
        let endCapWidth = this.getEndCapWidth();
        let endCapInstalledOffset = this.getEndCapInstalledOffset();
        return (
            chamberWidth * numOfChambers -
            chamberInstalledOffset * ( numOfChambers - 1 ) +
            ( endCapWidth - endCapInstalledOffset ) * 2
        );
    }

    calculatedBedWidth( numOfRows ) {
        let chamberHeight = this.getChamberHeight();
        let rowSpacing = this.getRowSpacing();
        return numOfRows * chamberHeight + rowSpacing * ( numOfRows - 1 );
    }

    getChamberHeight() {
        return (
            this.props.scalePixels *
            ( this.props.chamberType.width / this.props.scaleValue )
        );
    }

    getChamberWidth() {
        return (
            this.props.scalePixels *
            ( this.props.chamberType.length / this.props.scaleValue )
        );
    }

    getScaledValue( value ) {
        return getScaledValue(this.props.scalePixels, this.props.scaleValue, value);
    }

    getChamberInstalledOffset() {
        let width = this.getChamberWidth();
        let installedWidth =
            this.props.scalePixels *
            ( this.props.chamberType.installedLength / this.props.scaleValue );

        return width - installedWidth;
    }

    getEndCapHeight() {
        return (
            this.props.scalePixels *
            ( this.props.chamberEndCapType.width / this.props.scaleValue )
        );
    }

    getEndCapWidth() {
        return (
            this.props.scalePixels *
            ( this.props.chamberEndCapType.length / this.props.scaleValue )
        );
    }

    getEndCapInstalledOffset() {
        let width = this.getEndCapWidth();
        let installedWidth =
            this.props.scalePixels *
            ( this.props.chamberEndCapType.installedLength / this.props.scaleValue );

        return width - installedWidth;
    }

    getRowSpacing() {
        return (
            this.props.scalePixels *
            ( this.props.chamberRowSpacing / this.props.scaleValue )
        );
    }

    getEstimatedChamberBedDimensions( chamberRows, manifoldSeparations, debug = false ) {
        let {
            upperManifolds: upperManifoldsMetadata,
            lowerManifolds: lowerManifoldsMetadata,
            mainManifolds: mainManifoldsMetadata,
        } = this.props.getManifoldMetadata(chamberRows);

        let upperManifolds = this.props.getManifolds(chamberRows, upperManifoldsMetadata, true);
        let lowerManifolds = this.props.getManifolds(chamberRows, lowerManifoldsMetadata, false);

        if (chamberRows.length === 0) {
            return { points: [], length: 0, width: 0 };
        }

        const { chamberRowSpacing } = this.props;
        const { separationDistance } = this.props;

        // Compute the points
        let points = CalculationsLib.computePoints(
            null,
            chamberRows,
            this.getScaledValue(chamberRowSpacing),
            this.getScaledValue(separationDistance / 12),
            this.getScaledValue(this.getUnderdrainSeparationDistance(separationDistance)),
            this.props.hasUnderdrain,
            manifoldSeparations,
            upperManifolds,
            lowerManifolds,
            this.props.sepDistance,
            this.props.chamberType,
            this.props.chamberRowSpacing,
            this.props.scalePixels,
            this.props.scaleValue,
        );

        let xCoords = points.filter(( a, i ) => i % 2 === 0);
        let xMin = Math.min(...xCoords);
        let xMax = Math.max(...xCoords);
        let length = Math.abs(xMin - xMax);
        let unscaledLength = this.props.getUnscaledValue(length);

        let yCoords = points.filter(( a, i ) => i % 2 === 1);
        let yMin = Math.min(...yCoords);
        let yMax = Math.max(...yCoords);
        let width = Math.abs(yMin - yMax);
        let unscaledWidth = this.props.getUnscaledValue(width);

        if (debug) {
            console.log(
                "Est. Dimensions",
                `${unscaledWidth}x${unscaledLength}`,
                null,
                chamberRows,
                this.getScaledValue(chamberRowSpacing),
                this.getScaledValue(separationDistance / 12),
                this.getScaledValue(this.getUnderdrainSeparationDistance(separationDistance)),
                this.props.hasUnderdrain,
                manifoldSeparations,
                upperManifolds,
                lowerManifolds,
                points,
            );
        }
        return { points, length, width };
    };

    getEstimatedTotalVolume( chamberRows, manifoldSeparations, debug = false ) {
        if (chamberRows.length === 0) {
            return 0;
        }
        let points = this.getEstimatedChamberBedDimensions(chamberRows, manifoldSeparations).points;

        const { chamberType, chamberEndCapType } = this.props;

        let { porosityPercent } = this.props;
        porosityPercent /= 100;

        let { porousStoneAbove, porousStoneBelow } = this.props;
        porousStoneAbove /= 12;
        porousStoneBelow /= 12;

        let numberChambers = 0;
        let numberEndCaps = 0;
        for (let i = 0; i < chamberRows.length; ++i) {
            let activeChamberCnt = chamberRows[i].getActiveCount();
            let activeEndCapsCnt = chamberRows[i].countEndCaps();
            if (activeChamberCnt) {
                numberChambers += activeChamberCnt;
                numberEndCaps += activeEndCapsCnt;
            }
        }

        let area = this.computeStoneBedArea(points);
        let systemHeight = porousStoneBelow + chamberType.height + porousStoneAbove;
        let totalExcavation = area * systemHeight;
        let totalChamberStorage =
            chamberType.storage * numberChambers +
            chamberEndCapType.storage * numberEndCaps;

        let stoneAmountCalculated = ( totalExcavation - totalChamberStorage );
        let stoneVolumeCalculated = stoneAmountCalculated * porosityPercent;
        let installedStorageVolume = stoneVolumeCalculated + totalChamberStorage;

        if (debug) {
            console.log(
                "Est. Volume",
                area,
                systemHeight,
                totalExcavation,
                totalChamberStorage,
                stoneVolumeCalculated,
                installedStorageVolume,
                points,
            );
        }
        return installedStorageVolume;
    }

    computeStoneBedArea( stoneBedPoints ) {
        let points = [];
        for (let i = 0; i < stoneBedPoints.length - 2; i += 2) {
            let p = [stoneBedPoints[i], stoneBedPoints[i + 1]];
            points.push(p);
        }
        let area = calculatePolygonArea(points);
        /* convert to current scale square feets */
        area = area / Math.pow(this.props.scalePixels / this.props.scaleValue, 2);
        /* return the new stone bed area */
        return area;
    }

    addOutlet( previousChamberRows, structureType ) {
        if (this.props.hasOutlet && previousChamberRows.length > 1) {
            try {
                let chamberRows = cloneChamberRows(previousChamberRows);
                let lastChamberRow = chamberRows[chamberRows.length - 1];
                let lastActiveChamber = lastChamberRow ? lastChamberRow.getLastActiveChamber() : null;
                if (lastActiveChamber === null || lastActiveChamber === undefined) {
                    return chamberRows;
                }
                if (chamberRows.length === 1) {
                    // We can't add an outlet if the row already has an automatically placed isolator (since there's only a single row)
                    lastChamberRow.modifyChamber({
                        lowerCapActive: true,
                        lowerCapAccessory: null,
                    }, lastActiveChamber.col);
                } else {
                    lastChamberRow.modifyChamber({
                        lowerCapActive: true,
                        lowerCapAccessory: {
                            ...this.props.getChamberAccessoryMap().get(OUTLET_MANIFOLD_ID),
                            variant: structureType,
                            manifoldId: ManifoldUtils.generateManifoldId(),
                        },
                    }, lastActiveChamber.col);
                }
                return chamberRows;
            } catch (exception) {
                console.warn("There was a problem adding an outlet", exception);
            }
        }
        return cloneChamberRows(previousChamberRows);
    }

    getUnderdrainSeparationDistance( stoneBedSeparation ) {
        const minDistance = 1.6;
        return stoneBedSeparation / 12 >= minDistance ? 0 : minDistance - stoneBedSeparation / 12;
    }
}

const mapStateToProps = state => {

    const projectType = state.globals.projectType;
    let viewportScaleX, viewportScaleY;

    if (projectType === GLOBAL_PROJECT_TYPE_CHAMBER) {
        viewportScaleX = state.viewport.scaleX;
        viewportScaleY = state.viewport.scaleY;
    } else {
        viewportScaleX = state.viewport.scaleXPipes;
        viewportScaleY = state.viewport.scaleXPipes;
    }

    return {
        lang: state.localization.lang,
        viewportScaleX: viewportScaleX,
        viewportScaleY: viewportScaleY,
        chamberBedX: state.chamberbed.x,
        chamberBedY: state.chamberbed.y,
        chamberbed: state.chamberbed,
        content: state.localization.content,
        scaleValue: state.globals.scaleValue,
        scalePixels: state.globals.scalePixels,
        chamberType: state.globals.chamberType,
        hasOutlet: state.calculations.hasOutlet,
        chamberBedWidth: state.chamberbed.width,
        chamberBedHeight: state.chamberbed.height,
        chamberBedScaleX: state.chamberbed.scaleX,
        chamberBedScaleY: state.chamberbed.scaleY,
        hasUnderdrain: state.calculations.underdrain,
        numberOfRows: state.calculations.numberOfRows,
        chamberBedRotation: state.chamberbed.rotation,
        separationDistance: state.stonebed.sepDistance,
        storageVolume: state.calculations.storageVolume,
        availableWidth: state.calculations.availableWidth,
        generateSystem: state.calculations.generateSystem,
        chamberEndCapType: state.globals.chamberEndCapType,
        chamberRowSpacing: state.globals.chamberRowSpacing,
        porosityPercent: state.calculations.porosityPercent,
        availableLength: state.calculations.availableLength,
        manifoldPipeSizes: state.stonebed.manifoldPipeSizes,
        porousStoneAbove: state.calculations.porousStoneAbove,
        porousStoneBelow: state.calculations.porousStoneBelow,
        numberOfChambers: state.calculations.numberOfChambers,
    };
};

const mapDispatchToProps = {
    getManifolds,
    cleanChamberBed,
    getUnscaledValue,
    modalAlertActions,
    chamberRowsRebuild,
    endSystemGeneration,
    getManifoldMetadata,
    startVolumeOptimizer,
    chamberBedTransformed,
    getChamberAccessoryMap,
    applyManifoldSeparations,
    calculateManifoldSeparations,
    createMainManifold,
    clearMainManifoldsState,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(SystemBuilder);



// WEBPACK FOOTER //
// ./src/components/Builders/system-builder.js